import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icons";
import rigLaunchCard from "@/assets/rig-launch-card.webp";
import { OutreachDraftControl } from "@/features/outreach/OutreachDraftControl";
import { SignalActionForbiddenError } from "../../types";
import type { ActionHistoryEntry, FeedSignal, FoundSignalContact } from "../../types";
import type { SignalActionMutations } from "../../hooks";
import { abbreviateName, longDate, shortDate } from "../../format";
import { useRecipientMode } from "../../view-mode";
import {
  engagementLabel,
  hasActivationContent,
  relationshipLabel,
  valueTierLabel,
} from "../../activation";
import {
  INTERNAL_STAGE_LABELS,
  INTERNAL_STAGE_ORDER,
  NEXT_STAGE_EVENT,
  PREV_STAGE_EVENT,
  closedOutcomeOf,
  internalStageFromProjection,
  stageEventLabel,
  type InternalStageId,
} from "../../stage";

/**
 * Right column / activation rail (sealed detail mock, rail order per the
 * 2026-07-16 sealed amendment): zone 4 activation context (gold tint), the RKG
 * card, zone 6 outreach, zone 5 pursuit ownership (follow-up + Notes + outcome),
 * zone 7 pursuit stage, and the paginated action history.
 *
 * Backend-payoff wiring (this pass) brings the internal tier to parity with the
 * recipient page:
 *   - Zone 6 outreach hosts the LIVE Seamless "Find contacts outside the firm"
 *     search (find_contacts_proxy), claim-gated; "Save to contacts" persists via
 *     saveFoundContact. Draft Outreach is human-reviewed and live.
 *   - Zone 5 outcome records Won / Passed (record_outcome) with a confirm dialog,
 *     and renders the terminal WON / PASSED badge once the pursuit is closed.
 *   - Zone 7 stage bubbles are advance-only controls (record_stage_event): the
 *     next unreached bubble is the advance control with instant press feedback; a
 *     quiet "Correct" affordance offers walk-back / reopen corrections.
 * The current stage is read from the canonical get_signal_detail projection
 * (internalStageFromProjection over signal.canonicalStageKey) — the server
 * derives the stage from the ordered ledger, so no client derivation is needed.
 * Two gates, not one (R-2): FORWARD stage/outcome/find are enabled for the
 * signal's owner (claimer or assignee) OR any admin, mirroring the server's
 * canUseAssignedSignal; CORRECTIONS (walk-back and reopen) are admin/super_admin
 * only, mirroring the server's operator-only rule for stage_regressed and
 * reopen. A non-operator owner sees the ladder but no "Correct" affordance.
 *
 * Honest-data notes:
 *   - The RKG card is a static placeholder — no graph data is wired.
 *   - Notes is the deferred Notes slice: disabled, count 0.
 *   - Persisted find-contacts hydrate on open from signal.foundContacts (served
 *     by get_signal_detail with no search call); the Find button then reads
 *     "Refresh contact search" and re-runs find_contacts_proxy with refresh:true.
 */

const OUTREACH_LABELS = [
  "Find contacts outside the firm",
  "Draft Outreach",
  "Manage contacts in BDSE",
];

export function ModalRail({
  signal,
  viewerId,
  isAdmin,
  actions,
  history,
  historyLoading,
}: {
  signal: FeedSignal;
  viewerId: string;
  isAdmin: boolean;
  actions: SignalActionMutations;
  history: ActionHistoryEntry[];
  historyLoading: boolean;
}): JSX.Element {
  const recipient = useRecipientMode();
  const navigate = useNavigate();
  const activation = signal.activationContext ?? null;
  const [date, setDate] = useState("");

  const claimedByViewer = signal.ownershipState === "claimed" && signal.claimedBy === viewerId;
  const assignedToViewer = signal.ownershipState === "assigned" && signal.assignedTo === viewerId;
  const isViewers = claimedByViewer || assignedToViewer;
  // Server (canUseAssignedSignal) permits stage/outcome/find for the assignee or
  // claimer OR any admin. Mirror that so an admin can act on a signal they don't
  // personally own; non-admins stay claim-gated.
  const canAct = isViewers || isAdmin;
  // R-2: the joint gate is split. FORWARD stage advances stay owner-or-admin
  // (canAct above), matching requireAssignedSignal. CORRECTIONS are
  // operator-only: the server already rejects a non-admin stage_regressed and a
  // non-admin reopen with a 403, so a non-admin owner previously saw the
  // affordance, clicked, and ate a forbidden call. Gate it at the source.
  const canCorrect = isAdmin;

  const closedOutcome = closedOutcomeOf(signal);
  const isClosed = closedOutcome != null;
  const reachedStage = internalStageFromProjection(signal.canonicalStageKey);

  const todayStr = new Date().toISOString().slice(0, 10);
  const overdue =
    signal.followUpDueAt != null && new Date(signal.followUpDueAt).getTime() < Date.now();

  const submitFollowUp = () => {
    if (!date) return;
    // Local noon so the chosen day survives the round-trip to UTC ISO.
    const iso = new Date(`${date}T12:00:00`).toISOString();
    actions.setFollowUp.mutate(iso, { onSuccess: () => setDate("") });
  };

  return (
    <>
      {/* ZONE 4 · Activation context, gold tint — honest projection from
          get_signal_detail (v13). When the relationship is unresolved (null) or
          the payload predates the projection, the panel shows an honest empty
          state, never a default implying warmth. */}
      <div className="sx-z-activation">
        <p className="sx-zone-title sx-sm">Activation context</p>
        {hasActivationContent(activation) ? (
          <>
            <p className="sx-eyebrow">Relationship</p>
            <p className="sx-body sx-sm">{relationshipLabel(activation.relationship)}</p>
            <p className="sx-eyebrow">Detail</p>
            <p className="sx-body sx-sm">{engagementLabel(activation.engagementDetail)}</p>
            <p className="sx-eyebrow">Last touch</p>
            <p className="sx-body sx-sm">
              {activation.lastTouch ? longDate(activation.lastTouch) : "None"}
            </p>
            <p className="sx-eyebrow">Value tier</p>
            <p className="sx-body sx-sm">{valueTierLabel(activation.valueTier)}</p>
          </>
        ) : (
          <p className="sx-body sx-sm">No activation context on record for this signal.</p>
        )}
      </div>

      {/* RKG card — the Relationship Intelligence Graph launch button. In the
          authenticated tier it opens /rig for this signal; the recipient
          (Magic Link) tier has no access to the authenticated RIG, so it stays
          a static placeholder there. */}
      <button
        type="button"
        className="sx-rkg sx-rkg-framed"
        title={
          recipient
            ? "Relationship graph — coming soon"
            : "Open the Relationship Intelligence Graph"
        }
        onClick={recipient ? undefined : () => navigate(`/rig?signal=${signal.id}`)}
      >
        {/* Approved illustration (902×472, 1.911:1) IS the card's image region:
            full-bleed, edge to edge, its own warm ground reads as the image
            background. The clip inherits the card's top corner radius. */}
        <span className="sx-rkg-art">
          <img className="sx-rkg-img" src={rigLaunchCard} alt="" />
        </span>
        <span className="sx-rkg-text">
          <span className="sx-zone-title sx-sm" style={{ display: "block", marginBottom: 1 }}>
            Relationship Intelligence Graph
          </span>
          <span className="sx-helper" style={{ display: "block" }}>
            Explore contacts, signals, and pathways
          </span>
          {recipient ? null : <span className="sx-rkg-open">Open ↗</span>}
        </span>
      </button>

      {/* ZONE 6 · Outreach — live find-contacts home, claim-gated. */}
      {recipient ? null : (
        <OutreachZone
          signalId={signal.id}
          company={signal.company}
          canAct={canAct}
          actions={actions}
          storedContacts={signal.foundContacts ?? []}
        />
      )}

      {/* ZONE 5 · Pursuit ownership */}
      <div className="sx-z-pursuit">
        <p className="sx-zone-title sx-sm">Pursuit ownership</p>
        <p className="sx-eyebrow">Owner</p>
        <p className="sx-body sx-sm">
          <span className="sx-dot" />
          {ownerText(signal, viewerId)}
        </p>

        {signal.followUpDueAt ? (
          <div className="sx-followup">
            <p className="sx-eyebrow">Follow-up {overdue ? "overdue" : "due"}</p>
            <p className="sx-fu-date">{longDate(signal.followUpDueAt)}</p>
          </div>
        ) : null}

        {recipient ? null : (
          <>
            <p className="sx-eyebrow" style={{ marginBottom: 4 }}>
              {signal.followUpDueAt ? "Update follow-up" : "Set a follow-up"}
            </p>
            <div className="sx-date-row">
              <input
                className="sx-date-input"
                type="date"
                value={date}
                min={todayStr}
                aria-label="Follow-up date"
                onChange={(e) => setDate(e.target.value)}
              />
              <button
                type="button"
                className="sx-btn sx-btn-p2"
                style={{ padding: "6px 11px", fontSize: 12 }}
                disabled={!date || actions.setFollowUp.isPending}
                onClick={submitFollowUp}
              >
                {actions.setFollowUp.isPending
                  ? "Saving…"
                  : signal.followUpDueAt
                    ? "Update"
                    : "Set"}
              </button>
              {/* Notes — deferred Notes slice: rendered per the mock, disabled,
                  count 0 (no notes data to invent). */}
              <button
                type="button"
                className="sx-btn sx-btn-p2"
                style={{ padding: "6px 11px", fontSize: 12 }}
                disabled
                title="Notes — coming soon"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 5h16v11l-4 4H4z" />
                  <path d="M20 16h-4v4" />
                </svg>
                Notes <span className="sx-count">0</span>
              </button>
            </div>

            <MarkOutcome closedOutcome={closedOutcome} canAct={canAct} actions={actions} />
          </>
        )}
      </div>

      {/* ZONE 7 · Pursuit stage */}
      <PursuitStageZone
        reachedStage={reachedStage}
        isClosed={isClosed}
        canAct={canAct}
        canCorrect={canCorrect}
        recipient={recipient}
        actions={actions}
      />

      {/* Action history — collapsed one row; expands to 3 most recent, pages 3 at
          a time. Append-only ledger: no clear, no delete. */}
      <ActionHistory entries={history} loading={historyLoading} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone 6 · Outreach — live find-contacts home (Item 6)                        */
/* -------------------------------------------------------------------------- */
function OutreachZone({
  signalId,
  company,
  canAct,
  actions,
  storedContacts,
}: {
  signalId: string;
  company: string;
  canAct: boolean;
  actions: SignalActionMutations;
  storedContacts: FoundSignalContact[];
}): JSX.Element {
  const find = actions.findContacts;
  const save = actions.saveFoundContact;
  // Hydrate on open from the stored found_contacts (get_signal_detail, no search
  // call); a completed search supersedes them. hasResults therefore counts stored
  // rows too, so the button reads "Refresh contact search" from first render.
  const results: FoundSignalContact[] = find.data ?? storedContacts;
  const hasResults = results.length > 0;
  const [saved, setSaved] = useState<ReadonlySet<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);

  // Claim-gated: the sealed modal keeps the outreach zone inert until the signal
  // is the viewer's (or the viewer is an admin), matching the server gate.
  if (!canAct) {
    return (
      <div className="sx-z-outreach sx-unclaimed">
        <p className="sx-eyebrow">Outreach actions · claim to activate</p>
        <div className="sx-outreach-list">
          {OUTREACH_LABELS.map((label) => (
            <button
              key={label}
              type="button"
              className="sx-btn sx-btn-p2"
              disabled
              title="Claim the signal to open outreach"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="sx-gate-note">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5F6969"
            strokeWidth="1.8"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="4" y="11" width="16" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
          <p className="sx-helper">Claim the signal to open outreach.</p>
        </div>
      </div>
    );
  }

  const runFind = () => {
    if (find.isPending) return;
    // When results are already showing (stored on open, or a prior search), the
    // button refreshes with an external call; with nothing shown it runs the
    // initial stored-first search.
    find.mutate(hasResults ? { refresh: true } : undefined);
  };

  const onSave = (contact: FoundSignalContact) => {
    if (savingId) return;
    save.reset();
    setSavingId(contact.found_contact_id);
    save.mutate(contact.found_contact_id, {
      onSuccess: () => {
        setSaved((prev) => new Set(prev).add(contact.found_contact_id));
        setSavingId(null);
      },
      onError: () => setSavingId(null),
    });
  };

  return (
    <div className="sx-z-outreach sx-claimed">
      <p className="sx-eyebrow" style={{ color: "var(--action-text)" }}>
        Outreach actions
      </p>
      <div className="sx-outreach-list">
        <button
          type="button"
          className="sx-btn sx-btn-hero"
          disabled={find.isPending}
          onClick={runFind}
        >
          <Icon name="search" />
          {find.isPending
            ? "Searching…"
            : hasResults
              ? "Refresh contact search"
              : "Find contacts outside the firm"}
        </button>
        <OutreachDraftControl signalId={signalId} buttonClassName="sx-btn sx-btn-p2" />
        <a className="sx-btn sx-btn-p2" href="/contacts">
          <Icon name="address-book" />
          Manage contacts in BDSE
        </a>
      </div>

      {find.isIdle && results.length === 0 ? null : (
        <div className="sxr-outreach-contacts">
          {find.isPending ? (
            <div className="sxr-find-status" role="status">
              <span className="sxr-spinner" aria-hidden="true" />
              Searching outside the firm for new contacts at {company}.
            </div>
          ) : find.isError ? (
            <div className="sxr-find-status sxr-find-msg--err" role="status">
              Contact search could not run right now. Try again shortly.
            </div>
          ) : find.isSuccess && results.length === 0 ? (
            <div className="sxr-find-status sxr-find-msg" role="status">
              No new contacts found for {company} right now.
            </div>
          ) : hasResults ? (
            <div className="sxr-find-results">
              <p className="sxr-find-results-title">New contacts at {company}</p>
              {results.map((c) => (
                <FoundContactRow
                  key={c.id}
                  contact={c}
                  saved={saved.has(c.found_contact_id)}
                  saving={savingId === c.found_contact_id}
                  onSave={() => onSave(c)}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function FoundContactRow({
  contact,
  saved,
  saving,
  onSave,
}: {
  contact: FoundSignalContact;
  saved: boolean;
  saving: boolean;
  onSave: () => void;
}): JSX.Element {
  return (
    <div className="sxr-contact-row">
      <span className="sxr-avatar" aria-hidden="true">
        {initials(contact.display_name)}
      </span>
      <div className="sxr-contact-body">
        <div className="sxr-contact-name">{contact.display_name}</div>
        {contact.title ? <div className="sxr-contact-title">{contact.title}</div> : null}
      </div>
      <button
        type="button"
        className="sx-btn sx-btn-p3"
        disabled={saved || saving}
        onClick={onSave}
      >
        {saved ? "Saved" : saving ? "Saving…" : "Save to contacts"}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone 5 · Mark outcome (Items 2 + 5)                                         */
/* -------------------------------------------------------------------------- */
function MarkOutcome({
  closedOutcome,
  canAct,
  actions,
}: {
  closedOutcome: "won" | "passed" | null;
  canAct: boolean;
  actions: SignalActionMutations;
}): JSX.Element {
  const recordOutcome = actions.recordOutcome;
  const pending = recordOutcome.isPending;
  const [confirm, setConfirm] = useState<"won" | "passed" | null>(null);

  useEffect(() => {
    if (!confirm) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(".sxr-outcome-confirm")) {
        setConfirm(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setConfirm(null);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [confirm]);

  const openConfirm = (outcome: "won" | "passed") => {
    recordOutcome.reset();
    setConfirm(outcome);
  };
  const submit = () => {
    if (!confirm) return;
    recordOutcome.mutate({ outcome: confirm }, { onSuccess: () => setConfirm(null) });
  };

  return (
    <div className="sx-outcome">
      <p className="sx-eyebrow" style={{ marginBottom: 6 }}>
        Mark outcome
      </p>
      {closedOutcome != null ? (
        <span className={`sxr-outcome-badge sxr-outcome-badge--${closedOutcome}`}>
          {closedOutcome === "won" ? "WON" : "PASSED"}
        </span>
      ) : !canAct ? (
        <div className="sx-outcome-row">
          <button
            type="button"
            className="sx-btn sx-btn-p2"
            disabled
            title="Claim the signal to record an outcome"
          >
            Won
          </button>
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            disabled
            title="Claim the signal to record an outcome"
          >
            Passed
          </button>
        </div>
      ) : (
        <div className="sxr-outcome-confirm">
          <div className="sx-outcome-row">
            <button
              type="button"
              className="sx-btn sx-btn-p2"
              aria-haspopup="dialog"
              aria-expanded={confirm === "won"}
              disabled={pending}
              onClick={() => openConfirm("won")}
            >
              Won
            </button>
            <button
              type="button"
              className="sx-btn sx-btn-p3"
              aria-haspopup="dialog"
              aria-expanded={confirm === "passed"}
              disabled={pending}
              onClick={() => openConfirm("passed")}
            >
              Passed
            </button>
          </div>
          {confirm ? (
            <div
              className="sx-popover sx-popover--end"
              role="dialog"
              aria-label={`Confirm ${confirm === "won" ? "Won" : "Passed"} outcome`}
            >
              <div className="sx-popover-title">
                Mark this pursuit as {confirm === "won" ? "Won" : "Passed"}?
              </div>
              <p className="sx-popover-help">Recording an outcome closes the pursuit.</p>
              {recordOutcome.isError ? (
                <p className="sx-popover-help sx-popover-help--error">
                  Couldn&rsquo;t record — try again.
                </p>
              ) : null}
              <div className="sx-popover-actions">
                <button type="button" className="sx-btn sx-btn-p3" onClick={() => setConfirm(null)}>
                  Cancel
                </button>
                <button type="button" className="sx-cta" disabled={pending} onClick={submit}>
                  {pending ? "Recording…" : `Mark ${confirm === "won" ? "Won" : "Passed"}`}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
      <p className="sx-helper" style={{ marginTop: 7 }}>
        {closedOutcome != null
          ? "Outcome recorded."
          : canAct
            ? "Recording an outcome closes the pursuit."
            : "Available once the signal is claimed."}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Zone 7 · Pursuit stage — advance-only bubbles + corrections (Items 3 + 4)   */
/* -------------------------------------------------------------------------- */
function PursuitStageZone({
  reachedStage,
  isClosed,
  canAct,
  canCorrect,
  recipient,
  actions,
}: {
  reachedStage: InternalStageId;
  isClosed: boolean;
  canAct: boolean;
  canCorrect: boolean;
  recipient: boolean;
  actions: SignalActionMutations;
}): JSX.Element {
  const recordStage = actions.recordStage;
  const pending = recordStage.isPending;

  const won = reachedStage === "won";
  const reachedIndex = INTERNAL_STAGE_ORDER.indexOf(reachedStage);
  const doneCount = won ? INTERNAL_STAGE_ORDER.length : reachedIndex + 1;
  const nextIndex = isClosed ? -1 : doneCount;
  const nextEvent = isClosed ? undefined : NEXT_STAGE_EVENT[reachedStage];
  const interactive = !recipient && canAct;

  const stageError = recordStage.error;
  const conflict =
    recordStage.isError &&
    /\b409\b/.test(stageError instanceof Error ? stageError.message : String(stageError ?? ""));

  // Advance-only: the next unreached bubble is itself the control, recording the
  // immediate forward transition. Backward moves go through the Correct popover.
  const advance = () => {
    if (pending || !nextEvent) return;
    recordStage.reset();
    recordStage.mutate({ eventType: nextEvent });
  };

  return (
    <div className="sx-z-stage">
      <p className="sx-zone-title sx-sm">Pursuit stage</p>
      <ul className="sx-steps">
        {INTERNAL_STAGE_ORDER.map((stage, i) => {
          const cls = i < doneCount ? " sx-done" : i === nextIndex ? " sx-next" : "";
          const isNextControl = i === nextIndex && nextEvent != null && interactive;
          const pipPending = isNextControl && pending;
          const pip = (
            <span className={`sx-pip${pipPending ? " sx-pip--pending" : ""}`}>
              {i < doneCount ? (
                <svg
                  width="7"
                  height="7"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : null}
            </span>
          );
          const name = <span className="sx-s-name">{INTERNAL_STAGE_LABELS[stage]}</span>;
          return (
            <li key={stage} className={`sx-step${cls}`}>
              {isNextControl ? (
                <button
                  type="button"
                  className="sxr-step-btn"
                  disabled={pending}
                  title={`Record: ${INTERNAL_STAGE_LABELS[stage]}`}
                  aria-label={`Record pursuit stage: ${INTERNAL_STAGE_LABELS[stage]}`}
                  onClick={advance}
                >
                  {pip}
                  {name}
                </button>
              ) : (
                <>
                  {pip}
                  {name}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {interactive && canCorrect ? (
        <CorrectControl reachedStage={reachedStage} isClosed={isClosed} actions={actions} />
      ) : null}

      <p
        className="sx-helper"
        role="status"
        style={recordStage.isError && !conflict ? { color: "var(--gold-ink)" } : undefined}
      >
        {recordStage.isError
          ? conflict
            ? "Already recorded — reopen the signal to see the latest."
            : "Couldn't record that step — try again."
          : pending
            ? "Recording…"
            : isClosed
              ? "This pursuit is closed."
              : interactive && nextEvent != null
                ? "Click the next stage to advance. Each step is recorded to the signal history."
                : "Advance when it happens. Each step is recorded to the signal history."}
      </p>
    </div>
  );
}

/**
 * Quiet "Correct" affordance near the pursuit-stage zone (Item 4). Offers
 * "Walk back last stage" (stage_regressed) when a stage can be regressed, and
 * "Reopen pursuit" (outcome_reopened) when the pursuit is closed. Each has an
 * optional reason and a confirm step. The stage bubbles themselves stay
 * advance-only — no backward clicks.
 */
function CorrectControl({
  reachedStage,
  isClosed,
  actions,
}: {
  reachedStage: InternalStageId;
  isClosed: boolean;
  actions: SignalActionMutations;
}): JSX.Element | null {
  const recordStage = actions.recordStage;
  const recordOutcome = actions.recordOutcome;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"menu" | "regress" | "reopen">("menu");
  const [reason, setReason] = useState("");

  const prevEvent = PREV_STAGE_EVENT[reachedStage];
  const canRegress = !isClosed && prevEvent != null;
  const canReopen = isClosed;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(".sx-correct")) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!canRegress && !canReopen) return null;

  function close() {
    setOpen(false);
    setMode("menu");
    setReason("");
    recordStage.reset();
    recordOutcome.reset();
  }

  const regressLabel = prevEvent
    ? `Walk back to ${stageEventLabel(prevEvent) ?? "the previous stage"}`
    : "Walk back last stage";

  const submitRegress = () => {
    if (!prevEvent) return;
    recordStage.reset();
    recordStage.mutate(
      {
        eventType: "stage_regressed",
        toStage: prevEvent,
        reason: reason.trim() || undefined,
      },
      { onSuccess: close }
    );
  };
  const submitReopen = () => {
    recordOutcome.reset();
    recordOutcome.mutate(
      { outcome: "reopen", reason: reason.trim() || undefined },
      { onSuccess: close }
    );
  };

  return (
    <div className="sx-correct" style={{ position: "relative", marginBottom: 8 }}>
      <button
        type="button"
        className="sx-btn sx-btn-p3 sx-btn-quiet"
        style={{ fontSize: 12 }}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        Correct
      </button>
      {open ? (
        <div
          className="sx-popover sx-popover--end"
          role="dialog"
          aria-label="Correct pursuit stage or outcome"
        >
          {mode === "menu" ? (
            <>
              <div className="sx-popover-title">Correct the record</div>
              {canRegress ? (
                <button type="button" className="sx-menu-item" onClick={() => setMode("regress")}>
                  {regressLabel}
                </button>
              ) : null}
              {canReopen ? (
                <button type="button" className="sx-menu-item" onClick={() => setMode("reopen")}>
                  Reopen pursuit
                </button>
              ) : null}
              <p className="sx-popover-help">
                Corrections are recorded as append-only events; nothing is erased.
              </p>
            </>
          ) : mode === "regress" ? (
            <>
              <div className="sx-popover-title">{regressLabel}?</div>
              <textarea
                className="sx-field"
                value={reason}
                placeholder="Reason (optional)"
                aria-label="Reason for walking back the stage"
                onChange={(e) => setReason(e.target.value)}
              />
              {recordStage.isError ? (
                <p className="sx-popover-help sx-popover-help--error">
                  {recordStage.error instanceof SignalActionForbiddenError
                    ? "Stage corrections are limited to operators — ask an admin to walk this back."
                    : "Couldn’t record — try again."}
                </p>
              ) : null}
              <div className="sx-popover-actions">
                <button type="button" className="sx-btn sx-btn-p3" onClick={() => setMode("menu")}>
                  Back
                </button>
                <button
                  type="button"
                  className="sx-cta"
                  disabled={recordStage.isPending}
                  onClick={submitRegress}
                >
                  {recordStage.isPending ? "Recording…" : "Walk back"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="sx-popover-title">Reopen this pursuit?</div>
              <p className="sx-popover-help">
                Reopening returns the pursuit to active and clears the outcome.
              </p>
              <textarea
                className="sx-field"
                value={reason}
                placeholder="Reason (optional)"
                aria-label="Reason for reopening the pursuit"
                onChange={(e) => setReason(e.target.value)}
              />
              {recordOutcome.isError ? (
                <p className="sx-popover-help sx-popover-help--error">
                  {recordOutcome.error instanceof SignalActionForbiddenError
                    ? "Reopening is limited to operators — ask an admin to reopen this."
                    : "Couldn’t record — try again."}
                </p>
              ) : null}
              <div className="sx-popover-actions">
                <button type="button" className="sx-btn sx-btn-p3" onClick={() => setMode("menu")}>
                  Back
                </button>
                <button
                  type="button"
                  className="sx-cta"
                  disabled={recordOutcome.isPending}
                  onClick={submitReopen}
                >
                  {recordOutcome.isPending ? "Reopening…" : "Reopen"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Action history · sealed collapsed→3→show-more pagination                    */
/* -------------------------------------------------------------------------- */
const VERB_PHRASE: Record<string, string> = {
  claim: "Claimed the signal",
  unclaim: "Released the claim",
  assign: "Assigned the signal",
  share: "Created a Magic Link",
  archive_with_reason: "Archived the signal",
  follow_up_set: "Set a follow-up",
  relationship_check_run: "Ran a relationship check",
  tariff_scan_run: "Ran a Tariff Terrain scan",
  asset_sent: "Sent an asset",
  context_note: "Added context",
  magic_link_open: "Opened the Magic Link",
  signal_saved: "Saved the signal",
  signal_unsaved: "Removed from saved",
  contact_search_run: "Ran a contact search",
  stage_outreach_made: "Recorded outreach made",
  stage_meeting_scheduled: "Recorded a meeting",
  stage_proposal: "Recorded a proposal",
  outcome_recorded: "Recorded the outcome",
  outcome_reopened: "Reopened the pursuit",
};

/** Honest phrasing for a history entry, including the correction events (Item 4). */
function historyPhrase(entry: ActionHistoryEntry): string {
  if (entry.verb === "stage_regressed") {
    const label = stageEventLabel(entry.detail);
    return label ? `Walked back to ${label}` : "Walked back a stage";
  }
  return VERB_PHRASE[entry.verb] ?? entry.verb;
}

const HIST_PAGE = 3;

function ActionHistory({
  entries,
  loading,
}: {
  entries: ActionHistoryEntry[];
  loading: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(HIST_PAGE);

  // Newest first.
  const ordered = [...entries].reverse();
  const shown = ordered.slice(0, visible);
  const remaining = Math.max(0, ordered.length - visible);

  const collapse = () => {
    setOpen(false);
    setVisible(HIST_PAGE);
  };

  return (
    <div className="sx-history" data-open={open}>
      <button
        type="button"
        className="sx-strip"
        aria-expanded={open}
        onClick={() => (open ? collapse() : setOpen(true))}
      >
        <span className="sx-s-label">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5E7472"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          Action history
        </span>
        {entries.length > 0 ? <span className="sx-count">{entries.length}</span> : null}
      </button>

      {open ? (
        <div className="sx-hist-panel">
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "10px 0" }}>
              <span className="sx-skel" style={{ width: "80%" }} />
              <span className="sx-skel" style={{ width: "60%" }} />
            </div>
          ) : ordered.length === 0 ? (
            <p className="sx-hist-empty">No actions yet. Claiming this signal is the first.</p>
          ) : (
            <>
              <ol className="sx-hist-list">
                {shown.map((e) => (
                  <li key={e.id}>
                    <span className="sx-hist-what">{historyPhrase(e)}</span>
                    <span className="sx-hist-when">
                      {historyActor(e.actorName)} · {shortDate(e.at).toUpperCase()}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="sx-hist-foot">
                {remaining > 0 ? (
                  <button
                    type="button"
                    className="sx-btn sx-btn-p3"
                    onClick={() => setVisible((v) => v + HIST_PAGE)}
                  >
                    Show {Math.min(HIST_PAGE, remaining)} more
                  </button>
                ) : null}
                <button type="button" className="sx-btn sx-btn-p3 sx-btn-quiet" onClick={collapse}>
                  Collapse
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/** "David Whitfield" → "D. WHITFIELD"; single word → uppercased as-is. */
function historyActor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return name.toUpperCase();
  if (parts.length === 1) return first.toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return `${first.charAt(0)}. ${last}`.toUpperCase();
}

function ownerText(signal: FeedSignal, viewerId: string): string {
  if (signal.ownershipState === "unclaimed") return "Unclaimed";
  if (signal.ownershipState === "claimed") {
    return signal.claimedBy === viewerId ? "You, claimed" : abbreviateName(signal.claimedByName);
  }
  return signal.assignedTo === viewerId
    ? "Assigned to you"
    : `Assigned to ${abbreviateName(signal.assignedToName)}`;
}
